// core
import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { FormGroup } from '@angular/forms';

// Rxjs
import { Subscription } from "rxjs";
import { distinctUntilChanged, debounceTime, startWith } from 'rxjs/operators';

// constants
import { RAPTOR_DEBOUNCE_TIME, RAPTOR_MEDICAL_VALUE_KEY } from '../../../constant/raptor-constants';
// services
import { RaptorDataService } from '../../../services/raptor-data.service';
import { RaptorControlService } from '../../../services/raptor-control.service';
import { RaptorStepService } from '../../../services/raptor-step.service';
import { RaptorAnalyticsService } from '../../../services/raptor-analytics.service';
import { RaptorPredicateService } from '../../../services/raptor-predicate.service';

@Component({
  selector: 'open-raptor-repeater-question',
  templateUrl: './raptor-repeater-question.component.html',
  styleUrls: ['./raptor-repeater-question.component.scss']
})
export class RaptorRepeaterQuestionComponent implements OnInit, OnDestroy {
  @Input() question!: any;
  @Input() form!: FormGroup;

  questionControl: any;
  editing!: Boolean;
  submitted!: Boolean;
  questionValueWatcher!: Subscription;
  formValueWatcher!: Subscription;
  columns: number = 1;
  repeaterQuestions: Array<any> = [];

  constructor(
    public raptorDataService: RaptorDataService,
    private raptorControlService: RaptorControlService,
    private raptorStepService: RaptorStepService,
    private raptorAnalyticsService: RaptorAnalyticsService,
    private raptorPredicateService: RaptorPredicateService
  ) { }

  ngOnInit(): void {
    let changedField: any;
    let layoutConfig = this.raptorDataService.columnLayout(
      this.question.settings.columns,
      this.question.settings.rows,
      this.question.elements,
      this.question.settings.components,
      this.question.key
    );

    this.repeaterQuestions = layoutConfig.questions;
    this.columns = layoutConfig.columns;
    this.questionControl = this.form.get(this.question.key);

    this.questionValueWatcher = this.questionControl.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe((data: any) => {
        if (typeof data === 'string') {
          if (data !== 'loaded') {
            this.raptorAnalyticsService.trackAdditionalEvents('select-option', data, this.question.key);
          }
          if (data.indexOf('edit') > -1) {
            this.editing = true;
            // remove validators when starting edit mode
            this.raptorControlService.removeRepeaterValidators(this.question, this.form);
            this.raptorStepService.editRepeater(this.question.key, true);
            // mount data onto the questions in edit
            this.initialiseQuestions();
          }

          if (
            (data.indexOf('delete') > -1 || data.indexOf('cancel') > -1) &&
            (!this.question.data || !this.question.data.length)
          ) {
            this.submitted = false;
            this.initialiseValue('edit_0');
          }
        }
      });

    this.formValueWatcher = this.form.valueChanges
      .pipe(
        startWith(this.form.value),
        debounceTime(RAPTOR_DEBOUNCE_TIME),
        distinctUntilChanged((previous, current) => {
          changedField = this.raptorControlService.formValueChanged(previous, current);
          return !changedField;
        })
      )
      .subscribe(data => {
        if (this.editing && this.submitted) {
          this.raptorControlService.setRepeaterValidators(this.question, {}, this.form, this.form.getRawValue());
        }
      });

    if (!this.question.data || !this.question.data.length) {
      this.initialiseValue('edit_0');
    } else {
      this.initialiseValue('loaded');
    }
  }

  ngOnDestroy(): void {
    if (this.questionValueWatcher) {
      this.questionValueWatcher.unsubscribe();
    }
    if (this.formValueWatcher) {
      this.formValueWatcher.unsubscribe();
    }
  }

  initialiseValue(value: any): void {
    setTimeout(() => {
      this.questionControl.setValue(value, { emitEvent: true });
      this.editing = value !== 'loaded';
      if (value !== 'loaded') {
        this.raptorStepService.editRepeater(this.question.key, true);
      }
    }, RAPTOR_DEBOUNCE_TIME);
  }

  initialiseQuestions() {
    let index = parseInt(this.questionControl.value.split('_').pop(), 10);

    if (Array.isArray(this.question.elements)) {
      this.question.elements.forEach((element: any) => {
        if (element.type === 'medical_screening') {
          element.conditions = null;
          if (this.question.data && this.question.data[index]) {
            element.conditions = this.question.data[index][element.key + RAPTOR_MEDICAL_VALUE_KEY];
          }
        }
      });
    }
  }

  public cancel(): void {
    let index = parseInt(this.questionControl.value.split('_').pop(), 10);

    this.questionControl.setValue('cancel_' + index, { emitEvent: true });
    this.raptorStepService.editRepeater(this.question.key, false);
    this.editing = false;
  }

  public save(): void  {
    let index = parseInt(this.questionControl.value.split('_').pop(), 10);

    // add vlaidation and check it before saving
    this.submitted = true;
    this.raptorControlService.setRepeaterValidators(this.question, {}, this.form, this.form.getRawValue());

    if (this.repeaterValid()) {
      this.question.elements.forEach((element: any) => {
        let elementControl = this.form.get(element.key);
        // check if question is visible
        if (elementControl) {
          this.question.data = this.question.data || [];
          if (!this.question.data[index]) {
            this.question.data[index] = {};
          }
          this.question.data[index][element.key] = elementControl.value;

          // add human for question with choices
          let choices = element.settings && element.settings.choices;
          if (choices) {
            this.question.human = this.question.human || [];
            if (!this.question.human[index]) {
              this.question.human[index] = {};
            }
            this.question.human[index][element.key] = choices.find((choice: any) => choice.value === elementControl?.value)?.label
          }
        }
      });
      // wait for the last edited question in the repeater to fire the form value changes event
      setTimeout(() => {
        this.questionControl.setValue('save_' + index, { emitEvent: true });
        this.raptorStepService.editRepeater(this.question.key, false);
        this.editing = false;
        this.submitted = false;
      }, RAPTOR_DEBOUNCE_TIME);
    }
  }

  public add(): void  {
    let index = this.question.data && this.question.data.length ? this.question.data.length : 0;
    this.questionControl.setValue('edit_' + index, { emitEvent: true });
  }

  public repeaterValid(): boolean {
    let valid = true;

    this.question.elements.forEach((element: any) => {
      let elementControl = this.form.get(element.key);

      if (elementControl && elementControl.status !== 'VALID') {
        valid = false;
      }
    });
    return valid;
  }

  public maxItems(): boolean {
    let isMax = false;

    if (this.question.settings && this.question.settings.max_items) {
      let maxItemControl = this.form.get(this.question.settings.max_items);
      let itemCount = this.question.data && this.question.data.length ? this.question.data.length : 0;
      if (maxItemControl && maxItemControl.value) {
        isMax = itemCount >= parseInt(maxItemControl.value, 10);
      }
    }
    return isMax;
  }

  showQuestion(question: any): boolean {
    let isShown = true;
    if (question.show_if && typeof question.show_if === 'object') {
      isShown = this.raptorPredicateService.validator(question.show_if, this.form.value);
    }
    return isShown;
  }
}
